import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { OpsPresenceService, OpsMessageError } from './ops-presence.service';
import { OPS_STATUS_LABELS, OpsContact, OpsStatus } from './ops-presence.model';
import { ChatNotificationService } from '../../../core/chat/chat-notification.service';
import { ChatSummaryDto } from '../../../core/chat/chat.dto';

/**
 * Ops-team presence cluster for the portal header: one bubble per ops person
 * with a Teams-style status dot, opening a panel with office hours and a way to
 * message the team. Data is mocked today — see OpsPresenceService.
 */
@Component({
  selector: 'app-ops-presence',
  templateUrl: './ops-presence.component.html',
  styleUrls: ['./ops-presence.component.scss'],
})
export class OpsPresenceComponent implements OnInit, OnDestroy {
  @ViewChild(MatMenuTrigger) menuTrigger?: MatMenuTrigger;
  @Input() gpId: string | null = null;
  @Input() vehicleId: string | null = null;

  contacts: OpsContact[] = [];
  isLoading = false;

  /**
   * The panel is a three-step surface: the roster, the composer, and the
   * confirmation. Kept as a mode rather than separate overlays so the panel
   * doesn't jump position between steps.
   */
  panelMode: 'roster' | 'compose' | 'sent' = 'roster';
  draft = '';
  isSending = false;
  sendError = '';
  sentChat: ChatSummaryDto | null = null;

  // Bubbles beyond this collapse into a "+N" chip so the header can't be
  // pushed around by a large ops team.
  private static readonly MAX_BUBBLES = 4;

  private presenceSub?: Subscription;

  constructor(
    private opsPresenceService: OpsPresenceService,
    private chatNotifications: ChatNotificationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadTeam();
  }

  ngOnDestroy(): void {
    // The presence feed never completes — it has to be torn down by hand.
    this.presenceSub?.unsubscribe();
  }

  /**
   * Subscribes to the presence feed for the session. It keeps emitting (the
   * mock rerolls on a timer; the real feed will push), so contacts are simply
   * replaced on each emission. Order is deliberately left as the source gives
   * it: bubbles that hold their position and only change colour read as live
   * presence, whereas re-sorting by status makes the cluster jump every tick.
   */
  private loadTeam(): void {
    this.isLoading = true;
    this.presenceSub = this.opsPresenceService.getOpsTeam().subscribe({
      next: contacts => {
        this.contacts = contacts ?? [];
        this.isLoading = false;
      },
      // Presence is ambient information: a failure hides the cluster rather
      // than interrupting the user with an error.
      error: () => {
        this.contacts = [];
        this.isLoading = false;
      },
    });
  }

  get visibleContacts(): OpsContact[] {
    return this.contacts.slice(0, OpsPresenceComponent.MAX_BUBBLES);
  }

  get overflowCount(): number {
    return Math.max(0, this.contacts.length - OpsPresenceComponent.MAX_BUBBLES);
  }

  get activeCount(): number {
    return this.contacts.filter(c => c.status === 'active').length;
  }

  /** Header line for the panel, e.g. "1 of 3 available now". */
  get availabilitySummary(): string {
    if (!this.contacts.length) {
      return 'No ops contacts assigned';
    }
    return `${this.activeCount} of ${this.contacts.length} available now`;
  }

  // Each emission is a fresh array of fresh objects, so without this the
  // bubbles would be torn down and rebuilt every tick instead of just
  // recolouring their dot.
  trackById(_index: number, contact: OpsContact): string {
    return contact.id;
  }

  statusLabel(status: OpsStatus): string {
    return OPS_STATUS_LABELS[status];
  }

  initials(contact: OpsContact): string {
    return contact.name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map(part => part[0].toUpperCase())
      .join('');
  }

  startCompose(): void {
    this.panelMode = 'compose';
    this.sendError = '';
  }

  cancelCompose(): void {
    this.panelMode = 'roster';
    this.draft = '';
    this.sendError = '';
  }

  onMenuClosed(): void {
    this.panelMode = 'roster';
    this.draft = '';
    this.sendError = '';
    this.isSending = false;
    this.sentChat = null;
  }

  onComposerKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') {
      event.stopPropagation();
    }
  }

  send(): void {
    const body = this.draft.trim();
    if (!body || this.isSending || !this.vehicleId) {
      return;
    }

    this.isSending = true;
    this.sendError = '';
    this.opsPresenceService
      .messageOpsTeam(this.vehicleId, this.gpId, body)
      .subscribe({
        next: chat => {
          this.sentChat = chat;
          this.draft = '';
          this.panelMode = 'sent';
        },
        error: (error: OpsMessageError | unknown) => {
          this.isSending = false;
          this.sendError =
            (error as OpsMessageError)?.reason === 'NO_FUND_MAPPING'
              ? 'This vehicle has no verified ACE fund, so Ops has no thread to post to. Open the chat page to start one manually.'
              : 'Could not send your message. Try again, or open the chat page.';
        },
        complete: () => {
          this.isSending = false;
        },
      });
  }

  openSentChat(): void {
    const chatId = this.sentChat?.id;
    if (!chatId) return;
    this.menuTrigger?.closeMenu();
    this.chatNotifications.openChatById(chatId);
  }

  openSupportChat(): void {
    if (!this.gpId || !this.vehicleId) return;
    this.menuTrigger?.closeMenu();
    this.router.navigate(
      ['/gp', this.gpId, 'vehicle', this.vehicleId, 'chat'],
      { queryParams: { openSupport: Date.now() } }
    );
  }
}
